import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  LocalFireDepartment,
  Loop,
  MonitorWeight,
  Opacity,
  Schedule,
} from '@mui/icons-material';
import { tokens } from '../../theme';
import {
  adminTokens,
  caseTone,
  formatCaseCreatedAt,
  formatCompactDate,
  formatDisplayCaseId,
  formatMessageTime,
  formatRelativeTime,
  formatRelativeTimeLong,
  panelSx,
} from './adminTokens';
import {
  clientContextMetadataRows,
  formatEntitlementLabel,
  formatStoreLabel,
  getEffectiveRenewalStatus,
  getRevenuePlatformUrl,
  hasClientContext,
  subscriptionAuditSidebarRows,
} from '../../utils/adminDisplay';

const { border: BORDER, editorialSerif } = adminTokens;

const priorityWeights = { critical: 4, high: 3, medium: 2, low: 1 };
const QUEUE_PAGE_SIZE = 8;

function formatFileSize(size) {
  if (!size) return '0 B';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function isImageAttachment(attachment) {
  return Boolean(attachment?.mimeType?.startsWith('image/'));
}

function formatObjectValue(value) {
  if (value == null || value === '') return null;
  if (Array.isArray(value)) return value.length ? value.join(', ') : null;
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function formatObjectLabel(key) {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function buildObjectRows(value, preferredKeys = []) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
  const seen = new Set();
  const rows = [];

  preferredKeys.forEach((key) => {
    const formattedValue = formatObjectValue(value[key]);
    if (formattedValue) {
      rows.push([formatObjectLabel(key), formattedValue]);
      seen.add(key);
    }
  });

  Object.entries(value).forEach(([key, entryValue]) => {
    if (seen.has(key)) return;
    const formattedValue = formatObjectValue(entryValue);
    if (formattedValue) rows.push([formatObjectLabel(key), formattedValue]);
  });

  return rows;
}

function queueAccentColor(item) {
  if (item.statusLabel === 'Risk') return caseTone.risk;
  if (item.statusLabel === 'Review') return caseTone.review;
  return caseTone.open;
}

function CaseStatusBadge({ label }) {
  const styles = {
    Open: { fg: '#dff2e4', bg: 'rgba(123,196,142,0.22)', border: 'rgba(123,196,142,0.45)' },
    Review: { fg: '#f4e2cf', bg: 'rgba(212,144,99,0.22)', border: 'rgba(212,144,99,0.45)' },
    Risk: { fg: '#f7d5d1', bg: 'rgba(230,124,115,0.22)', border: 'rgba(230,124,115,0.45)' },
  };
  const style = styles[label] || styles.Open;
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        borderRadius: '2px',
        height: 20,
        fontSize: '0.62rem',
        fontWeight: 600,
        color: style.fg,
        bgcolor: style.bg,
        border: `1px solid ${style.border}`,
      }}
    />
  );
}

function buildCases(tickets, submissions, subscriptions, userLookup) {
  const supportCases = tickets
    .filter((t) => t.status !== 'resolved' && t.status !== 'closed')
    .map((t) => ({
      id: `ticket:${t.id}`,
      caseId: `CASE-${String(t.id).slice(-4).toUpperCase()}`,
      source: 'support',
      priority: t.priority || 'medium',
      status: t.status || 'open',
      statusLabel: t.priority === 'critical' ? 'Risk' : t.status === 'inProgress' ? 'Review' : 'Open',
      title: t.subject,
      subtitle: t.description,
      preview: t.clientContext?.crashFlag
        ? `Crash report · ${t.clientContext.currentScreen || t.subject}`
        : (t.clientContext?.currentScreen ? `${t.clientContext.currentScreen} · ${t.description}` : t.description),
      userId: t.userId,
      createdAt: t.createdAt,
      raw: t,
      crashFlag: Boolean(t.clientContext?.crashFlag),
    }));

  const submissionCases = submissions.map((s) => ({
    id: `submission:${s.id || s._id}`,
    caseId: `SUB-${String(s.id || s._id).slice(-4).toUpperCase()}`,
    source: 'submission',
    priority: 'medium',
    status: s.status || 'pending',
    statusLabel: 'Review',
    title: s.productName,
    subtitle: 'Food registry submission awaiting review.',
    preview: 'Submission pending nutrition review.',
    userId: s.submittedBy,
    createdAt: s.createdAt,
    raw: s,
  }));

  const subscriptionCases = subscriptions
    .filter((s) => {
      const renewal = getEffectiveRenewalStatus(s);
      return renewal !== 'active' || !s.isPremium;
    })
    .slice(0, 8)
    .map((s) => {
      const user = userLookup[s.userId];
      const renewal = getEffectiveRenewalStatus(s);
      return {
        id: `subscription:${s.userId}`,
        caseId: `BILL-${String(s.userId).slice(-4).toUpperCase()}`,
        source: 'subscription',
        priority: renewal === 'expired' || renewal === 'grace' ? 'high' : 'medium',
        status: renewal,
        statusLabel: renewal === 'expired' || renewal === 'grace' ? 'Risk' : 'Review',
        title: `${user?.name || user?.email || 'Customer'} subscription`,
        subtitle: `${formatEntitlementLabel(s)} · ${renewal}`,
        preview: `${formatEntitlementLabel(s)} on ${formatStoreLabel(s)} · ${renewal}`,
        userId: s.userId,
        createdAt: s.subscriptionLastUpdated || s.updatedAt,
        raw: s,
      };
    });

  return [...supportCases, ...submissionCases, ...subscriptionCases].sort((a, b) => {
    const gap = (priorityWeights[b.priority] || 0) - (priorityWeights[a.priority] || 0);
    if (gap !== 0) return gap;
    return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
  });
}

function AccountHealthItem({ label, ok }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box sx={{ width: 16, height: 16, display: 'grid', placeItems: 'center', border: `1px solid ${ok ? 'rgba(123,196,142,0.28)' : 'rgba(230,124,115,0.28)'}`, bgcolor: ok ? 'rgba(123,196,142,0.12)' : 'rgba(230,124,115,0.12)', color: ok ? tokens.success : tokens.danger, fontSize: '0.65rem' }}>
        {ok ? '✓' : '!'}
      </Box>
      <Typography variant="body2" sx={{ color: tokens.textSoft, fontSize: '0.82rem' }}>{label}</Typography>
    </Stack>
  );
}

function MetadataCell({ label, value, valueColor }) {
  if (value == null || value === '') return null;
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 1.25, alignItems: 'baseline', py: 0.35 }}>
      <Typography variant="caption" sx={{ color: tokens.textMuted, whiteSpace: 'nowrap' }}>{label}</Typography>
      <Typography variant="caption" sx={{ color: valueColor || tokens.cream, fontWeight: 500 }}>{value ?? '—'}</Typography>
    </Box>
  );
}

function ClientContextPanel({ context }) {
  if (!hasClientContext(context)) return null;
  const rows = clientContextMetadataRows(context).filter(([, value]) => value != null && value !== '');
  return (
    <Box sx={{ p: 1.5, border: `1px solid ${BORDER}`, bgcolor: 'rgba(255,255,255,0.02)', mb: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ color: tokens.cream, fontFamily: editorialSerif }}>Client context</Typography>
        {context.crashFlag && (
          <Chip
            label="Crash"
            size="small"
            sx={{
              borderRadius: '2px',
              height: 20,
              fontSize: '0.62rem',
              fontWeight: 600,
              color: '#f7d5d1',
              bgcolor: 'rgba(230,124,115,0.22)',
              border: '1px solid rgba(230,124,115,0.45)',
            }}
          />
        )}
      </Stack>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: '2px 28px',
        }}
      >
        {rows.map(([label, value]) => (
          <MetadataCell key={label} label={label} value={value} />
        ))}
      </Box>
      {context.reproSteps && (
        <Box sx={{ mt: 1.25, pt: 1.25, borderTop: `1px solid ${BORDER}` }}>
          <Typography variant="caption" sx={{ color: tokens.textMuted, display: 'block', mb: 0.5 }}>Repro steps</Typography>
          <Typography variant="body2" sx={{ color: tokens.textSoft, whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
            {context.reproSteps}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function EvidenceChip({ label }) {
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        borderRadius: 0,
        height: 22,
        fontSize: '0.68rem',
        bgcolor: 'rgba(255,255,255,0.04)',
        color: tokens.textSoft,
        border: `1px solid ${BORDER}`,
      }}
    />
  );
}

function ObjectMetadataPanel({ title, rows }) {
  if (!rows.length) return null;
  return (
    <Box sx={{ p: 1.5, border: `1px solid ${BORDER}`, bgcolor: 'rgba(255,255,255,0.02)' }}>
      <Typography variant="subtitle2" sx={{ color: tokens.cream, mb: 1, fontFamily: editorialSerif }}>{title}</Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: '2px 24px',
        }}
      >
        {rows.map(([label, value]) => (
          <MetadataCell key={label} label={label} value={value} />
        ))}
      </Box>
    </Box>
  );
}

function SupportEvidencePanel({ ticket }) {
  const context = ticket?.clientContext || {};
  const attachments = Array.isArray(ticket?.attachments) ? ticket.attachments : [];
  const consoleEvents = Array.isArray(context.browserConsole) ? context.browserConsole : [];
  const appInfoRows = buildObjectRows(ticket?.appInfo, ['version', 'appVersion', 'buildNumber']);
  const deviceInfoRows = buildObjectRows(ticket?.deviceInfo, ['platform', 'systemName', 'systemVersion', 'model', 'localizedModel', 'device', 'locale', 'language', 'userAgent']);
  const evidenceChips = [
    attachments.length ? `${attachments.length} attachment${attachments.length === 1 ? '' : 's'}` : null,
    consoleEvents.length ? `${consoleEvents.length} console event${consoleEvents.length === 1 ? '' : 's'}` : null,
    context.pageUrl ? 'Page URL captured' : null,
    context.viewport ? `Viewport ${context.viewport}` : null,
    context.currentScreen ? `Screen ${context.currentScreen}` : null,
  ].filter(Boolean);

  if (
    !attachments.length
    && !consoleEvents.length
    && !context.pageUrl
    && !context.viewport
    && !appInfoRows.length
    && !deviceInfoRows.length
  ) {
    return null;
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ color: tokens.cream, mb: 1.1, fontFamily: editorialSerif }}>Captured evidence</Typography>
      {evidenceChips.length > 0 && (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
          {evidenceChips.map((label) => (
            <EvidenceChip key={label} label={label} />
          ))}
        </Stack>
      )}

      {(context.pageUrl || context.viewport) && (
        <Box sx={{ p: 1.5, border: `1px solid ${BORDER}`, bgcolor: 'rgba(255,255,255,0.02)', mb: 1.25 }}>
          <Typography variant="subtitle2" sx={{ color: tokens.cream, mb: 0.9, fontFamily: editorialSerif }}>Web session</Typography>
          <Stack spacing={0.8}>
            {context.pageUrl && (
              <Box>
                <Typography variant="caption" sx={{ color: tokens.textMuted, display: 'block', mb: 0.25 }}>Page URL</Typography>
                <Link
                  href={context.pageUrl}
                  target="_blank"
                  rel="noreferrer"
                  underline="hover"
                  sx={{ color: tokens.accentStrong, wordBreak: 'break-all', fontSize: '0.82rem' }}
                >
                  {context.pageUrl}
                </Link>
              </Box>
            )}
            {context.viewport && (
              <Box>
                <Typography variant="caption" sx={{ color: tokens.textMuted, display: 'block', mb: 0.25 }}>Viewport</Typography>
                <Typography variant="body2" sx={{ color: tokens.textSoft }}>{context.viewport}</Typography>
              </Box>
            )}
          </Stack>
        </Box>
      )}

      {attachments.length > 0 && (
        <Box sx={{ p: 1.5, border: `1px solid ${BORDER}`, bgcolor: 'rgba(255,255,255,0.02)', mb: 1.25 }}>
          <Typography variant="subtitle2" sx={{ color: tokens.cream, mb: 0.9, fontFamily: editorialSerif }}>Attachments</Typography>
          <Stack spacing={1}>
            {attachments.map((attachment, index) => (
              <Box key={`${attachment.url || attachment.storagePath || attachment.originalName}-${index}`} sx={{ p: 1, border: `1px solid ${BORDER}` }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ color: tokens.cream }} noWrap>
                      {attachment.originalName || `Attachment ${index + 1}`}
                    </Typography>
                    <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                      {attachment.mimeType || 'file'} · {formatFileSize(attachment.size)}
                    </Typography>
                  </Box>
                  {attachment.url && (
                    <Link href={attachment.url} target="_blank" rel="noreferrer" underline="hover" sx={{ color: tokens.accentStrong }}>
                      Open file
                    </Link>
                  )}
                </Stack>
                {attachment.url && isImageAttachment(attachment) && (
                  <Box
                    component="img"
                    src={attachment.url}
                    alt={attachment.originalName || 'Support attachment'}
                    sx={{ mt: 1, width: '100%', maxHeight: 260, objectFit: 'cover', border: `1px solid ${BORDER}` }}
                  />
                )}
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {consoleEvents.length > 0 && (
        <Box sx={{ p: 1.5, border: `1px solid ${BORDER}`, bgcolor: 'rgba(255,255,255,0.02)', mb: 1.25 }}>
          <Typography variant="subtitle2" sx={{ color: tokens.cream, mb: 0.9, fontFamily: editorialSerif }}>Browser console</Typography>
          <Stack spacing={1}>
            {consoleEvents.map((entry, index) => (
              <Box key={`${entry.timestamp || 'console'}-${index}`} sx={{ p: 1, border: `1px solid ${BORDER}`, bgcolor: 'rgba(255,255,255,0.02)' }}>
                <Typography variant="caption" sx={{ color: tokens.textMuted, display: 'block', mb: 0.35 }}>
                  {(entry.level || 'log').toUpperCase()} · {entry.source || 'console'} · {formatCaseCreatedAt(entry.timestamp)}
                </Typography>
                <Typography variant="body2" sx={{ color: tokens.textSoft, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {entry.message}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      <Stack spacing={1.25}>
        <ObjectMetadataPanel title="App payload" rows={appInfoRows} />
        <ObjectMetadataPanel title="Device payload" rows={deviceInfoRows} />
      </Stack>
    </Box>
  );
}

function StatCell({ icon, label, value }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        px: 1.5,
        py: 1.25,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        borderRight: `1px solid ${BORDER}`,
        '&:last-child': { borderRight: 'none' },
      }}
    >
      <Box sx={{ color: tokens.accentStrong, display: 'flex', flexShrink: 0, '& svg': { fontSize: '1.15rem' } }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ color: tokens.cream, fontWeight: 600, lineHeight: 1.25 }} noWrap>{value ?? '—'}</Typography>
        <Typography variant="caption" sx={{ color: tokens.textMuted, display: 'block', lineHeight: 1.2 }}>{label}</Typography>
      </Box>
    </Box>
  );
}

function CaseQueueCard({ item, user, selected, onSelect }) {
  const accent = queueAccentColor(item);
  const attachmentCount = item.source === 'support' ? item.raw?.attachments?.length || 0 : 0;
  const consoleCount = item.source === 'support' ? item.raw?.clientContext?.browserConsole?.length || 0 : 0;
  const hasPageUrl = item.source === 'support' ? Boolean(item.raw?.clientContext?.pageUrl) : false;
  return (
    <Box
      onClick={onSelect}
      sx={{
        display: 'flex',
        cursor: 'pointer',
        borderBottom: `1px solid ${BORDER}`,
        bgcolor: selected ? 'rgba(169,191,122,0.08)' : 'transparent',
        '&:hover': { bgcolor: selected ? 'rgba(169,191,122,0.1)' : 'rgba(255,255,255,0.03)' },
      }}
    >
      <Box sx={{ width: 3, flexShrink: 0, bgcolor: accent, opacity: selected ? 1 : 0.75 }} />
      <Box sx={{ flex: 1, minWidth: 0, p: 1.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 0.75 }}>
          <Typography
            variant="body2"
            sx={{ color: tokens.cream, fontWeight: 500, fontSize: '0.84rem', lineHeight: 1.35, pr: 1 }}
            noWrap
          >
            {item.title}
          </Typography>
          <Typography variant="caption" sx={{ color: tokens.textMuted, flexShrink: 0, fontSize: '0.72rem' }}>
            {formatRelativeTime(item.createdAt)}
          </Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.55 }}>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
            <Avatar sx={{ width: 22, height: 22, fontSize: '0.65rem', bgcolor: 'rgba(255,255,255,0.08)' }}>
              {(user?.name || user?.email || 'U')[0].toUpperCase()}
            </Avatar>
            <Typography variant="caption" sx={{ color: tokens.textSoft, fontSize: '0.76rem' }} noWrap>
              {user?.name || 'Unknown'}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
            {item.crashFlag && (
              <Chip
                label="Crash"
                size="small"
                sx={{
                  borderRadius: '2px',
                  height: 18,
                  fontSize: '0.58rem',
                  fontWeight: 600,
                  color: '#f7d5d1',
                  bgcolor: 'rgba(230,124,115,0.18)',
                  border: '1px solid rgba(230,124,115,0.35)',
                }}
              />
            )}
            <CaseStatusBadge label={item.statusLabel} />
          </Stack>
        </Stack>
        <Typography
          variant="caption"
          sx={{
            color: tokens.textMuted,
            display: 'block',
            fontSize: '0.72rem',
            lineHeight: 1.45,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.preview}
        </Typography>
        {item.source === 'support' && (attachmentCount > 0 || consoleCount > 0 || hasPageUrl) && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.9 }}>
            {attachmentCount > 0 && <EvidenceChip label={`${attachmentCount} file${attachmentCount === 1 ? '' : 's'}`} />}
            {consoleCount > 0 && <EvidenceChip label={`${consoleCount} console`} />}
            {hasPageUrl && <EvidenceChip label="URL" />}
          </Stack>
        )}
      </Box>
    </Box>
  );
}

function ThreadMessage({ msg }) {
  const isSupport = msg.authorType === 'support';
  const isSystem = msg.authorType === 'system';
  const initial = (msg.author || 'U')[0].toUpperCase();
  return (
    <Box
      sx={{
        p: 1.25,
        border: `1px solid ${isSystem ? 'rgba(212,144,99,0.45)' : BORDER}`,
        bgcolor: isSupport ? 'rgba(169,191,122,0.07)' : isSystem ? 'rgba(212,144,99,0.06)' : 'rgba(255,255,255,0.02)',
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Avatar
          sx={{
            width: 28,
            height: 28,
            fontSize: '0.72rem',
            bgcolor: isSupport ? 'rgba(169,191,122,0.18)' : 'rgba(255,255,255,0.08)',
            color: isSupport ? tokens.accentStrong : tokens.cream,
          }}
        >
          {initial}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.35 }}>
            <Typography
              variant="caption"
              sx={{ color: isSupport ? tokens.accentStrong : isSystem ? tokens.clay : tokens.cream, fontWeight: 500 }}
            >
              {msg.author}
            </Typography>
            <Typography variant="caption" sx={{ color: tokens.textMuted, flexShrink: 0, ml: 1, fontSize: '0.68rem' }}>
              {formatMessageTime(msg.createdAt)}
            </Typography>
          </Stack>
          <Typography variant="body2" sx={{ color: tokens.cream, lineHeight: 1.55 }}>{msg.text}</Typography>
        </Box>
      </Stack>
    </Box>
  );
}

export default function AdminOperationsView({
  tickets,
  submissions,
  subscriptions,
  users,
  userProfiles,
  search,
  selectedCaseId,
  onSelectCase,
  replyDraft,
  onReplyChange,
  replyMode,
  onReplyModeChange,
  caseTags,
  onCaseTagsChange,
  queueFilter,
  onQueueFilterChange,
  queueSort,
  onQueueSortChange,
  updating,
  onSendReply,
  onInternalNote,
  onMarkInProgress,
  onApproveSubmission,
  onRejectSubmission,
  onGrantPremium,
  onMarkExpired,
  onSyncStore,
  onRefund,
  onAddCredit,
  onCancelSubscription,
  onSuspendAccount,
  onBanAccount,
  onNavigateOverview,
  onMakeAdmin,
  onRemoveAdmin,
}) {
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [tagDraft, setTagDraft] = useState('');
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const [creditAmountDollars, setCreditAmountDollars] = useState('9.99');

  const userLookup = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const subscriptionLookup = useMemo(() => {
    const map = Object.fromEntries(subscriptions.map((s) => [s.userId, s]));
    users.forEach((u) => { if (u.subscription && !map[u.id]) map[u.id] = u.subscription; });
    return map;
  }, [subscriptions, users]);

  const allCases = useMemo(() => buildCases(tickets, submissions, subscriptions, userLookup), [tickets, submissions, subscriptions, userLookup]);

  const filteredCases = useMemo(() => {
    let list = allCases;
    const term = search.trim().toLowerCase();
    if (term) {
      list = list.filter((item) => {
        const user = userLookup[item.userId] || {};
        return [item.title, item.subtitle, item.caseId, user.name, user.email].filter(Boolean).some((v) => String(v).toLowerCase().includes(term));
      });
    }
    if (queueFilter === 'open') list = list.filter((c) => c.statusLabel === 'Open' || c.status === 'open');
    else if (queueFilter === 'review') list = list.filter((c) => c.statusLabel === 'Review');
    else if (queueFilter === 'risk') list = list.filter((c) => c.statusLabel === 'Risk' || c.priority === 'critical');
    if (queueSort === 'oldest') list = [...list].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    return list;
  }, [allCases, search, userLookup, queueFilter, queueSort]);

  const selectedCase = filteredCases.find((c) => c.id === selectedCaseId) || allCases.find((c) => c.id === selectedCaseId) || null;
  const selectedUser = selectedCase ? userLookup[selectedCase.userId] || null : null;
  const selectedSubscription = selectedUser ? subscriptionLookup[selectedUser.id] || null : null;
  const selectedProfile = selectedUser ? userProfiles[selectedUser.id] : null;

  const threadMessages = useMemo(() => {
    if (!selectedCase || selectedCase.source !== 'support') return [];
    const ticket = selectedCase.raw;
    const msgs = [];
    msgs.push({ author: ticket.userName || ticket.userEmail, authorType: 'user', text: ticket.description, createdAt: ticket.createdAt });
    (ticket.messages || []).forEach((m) => msgs.push(m));
    if (ticket.adminResponse && !(ticket.messages || []).some((m) => m.text === ticket.adminResponse)) {
      msgs.push({ author: 'FastTime Support', authorType: 'support', text: ticket.adminResponse, createdAt: ticket.adminResponseAt });
    }
    if (selectedSubscription) {
      msgs.push({ author: 'System note', authorType: 'system', text: `Subscription status is ${selectedSubscription.status}. Premium is ${selectedSubscription.isPremium ? 'enabled' : 'not enabled'}.`, createdAt: selectedSubscription.subscriptionLastUpdated });
    }
    return msgs;
  }, [selectedCase, selectedSubscription]);

  const accountAgeDays = selectedUser?.createdAt
    ? Math.max(1, Math.floor((Date.now() - new Date(selectedUser.createdAt)) / 86400000))
    : null;

  const defaultTags = ['billing', 'subscription', 'premium', 'ios'];
  const activeTags = caseTags.length ? caseTags : (selectedCase?.raw?.tags || defaultTags);

  const [queuePage, setQueuePage] = useState(0);

  useEffect(() => {
    setQueuePage(0);
  }, [queueFilter, queueSort, search]);

  const queuePageCount = Math.max(1, Math.ceil(filteredCases.length / QUEUE_PAGE_SIZE));
  const safeQueuePage = Math.min(queuePage, queuePageCount - 1);
  const pagedCases = filteredCases.slice(safeQueuePage * QUEUE_PAGE_SIZE, safeQueuePage * QUEUE_PAGE_SIZE + QUEUE_PAGE_SIZE);
  const queueRangeStart = filteredCases.length ? safeQueuePage * QUEUE_PAGE_SIZE + 1 : 0;
  const queueRangeEnd = Math.min(filteredCases.length, (safeQueuePage + 1) * QUEUE_PAGE_SIZE);

  const displayCaseId = selectedCase
    ? formatDisplayCaseId(selectedCase.createdAt, selectedCase.raw?.id || selectedCase.id)
    : null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'stretch',
        flex: 1,
        minHeight: 0,
        gap: 2,
        overflow: 'hidden',
      }}
    >
      {/* Case Queue */}
      <Box sx={panelSx({ p: 0, display: 'flex', flexDirection: 'column', width: 260, flexShrink: 0, minHeight: 0, height: '100%' })}>
        <Box sx={{ p: 1.75, borderBottom: `1px solid ${BORDER}` }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="h6" sx={{ color: tokens.cream, fontFamily: editorialSerif }}>Case Queue</Typography>
            <Chip label={filteredCases.length} size="small" sx={{ borderRadius: 0, height: 20, bgcolor: 'rgba(169,191,122,0.12)', color: tokens.accentStrong }} />
          </Stack>
          <Stack direction="row" spacing={0.75}>
            <Select size="small" value={queueFilter} onChange={(e) => onQueueFilterChange(e.target.value)} sx={{ flex: 1, fontSize: '0.78rem', borderRadius: 0 }}>
              <MenuItem value="all">All Open</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="review">Review</MenuItem>
              <MenuItem value="risk">Risk</MenuItem>
            </Select>
            <Select size="small" value={queueSort} onChange={(e) => onQueueSortChange(e.target.value)} sx={{ flex: 1, fontSize: '0.78rem', borderRadius: 0 }}>
              <MenuItem value="newest">Sort: Newest</MenuItem>
              <MenuItem value="oldest">Sort: Oldest</MenuItem>
            </Select>
          </Stack>
        </Box>
        <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {pagedCases.map((item) => (
            <CaseQueueCard
              key={item.id}
              item={item}
              user={userLookup[item.userId]}
              selected={selectedCaseId === item.id}
              onSelect={() => onSelectCase(item.id)}
            />
          ))}
          {filteredCases.length === 0 && (
            <Box sx={{ p: 2 }}><Typography variant="body2" sx={{ color: tokens.textMuted }}>No cases match.</Typography></Box>
          )}
        </Box>
        <Box
          sx={{
            p: 1.25,
            borderTop: `1px solid ${BORDER}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography variant="caption" sx={{ color: tokens.textMuted, fontSize: '0.72rem' }}>
            {filteredCases.length
              ? `Showing ${queueRangeStart}-${queueRangeEnd} of ${filteredCases.length}`
              : 'No cases'}
          </Typography>
          <Stack direction="row" spacing={0.25}>
            <IconButton
              size="small"
              disabled={safeQueuePage === 0}
              onClick={() => setQueuePage((p) => Math.max(0, p - 1))}
              sx={{ borderRadius: 0, border: `1px solid ${BORDER}`, width: 28, height: 28 }}
            >
              <ChevronLeft sx={{ fontSize: '1rem' }} />
            </IconButton>
            <IconButton
              size="small"
              disabled={safeQueuePage >= queuePageCount - 1}
              onClick={() => setQueuePage((p) => Math.min(queuePageCount - 1, p + 1))}
              sx={{ borderRadius: 0, border: `1px solid ${BORDER}`, width: 28, height: 28 }}
            >
              <ChevronRight sx={{ fontSize: '1rem' }} />
            </IconButton>
          </Stack>
        </Box>
      </Box>

      {/* Case Detail */}
      <Box sx={panelSx({ p: 0, display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, minHeight: 0, height: '100%' })}>
        {selectedCase ? (
          <>
            <Box sx={{ p: 2, borderBottom: `1px solid ${BORDER}` }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box sx={{ minWidth: 0, pr: 2 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="h6" sx={{ color: tokens.cream, fontFamily: editorialSerif }}>#{displayCaseId}</Typography>
                    <CaseStatusBadge label={selectedCase.statusLabel} />
                  </Stack>
                  <Typography variant="body2" sx={{ color: tokens.textSoft, mb: 0.35, fontSize: '0.86rem' }} noWrap>
                    {selectedCase.title}
                  </Typography>
                  <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                    {formatCaseCreatedAt(selectedCase.createdAt)}
                  </Typography>
                </Box>
                <Button variant="outlined" size="small" onClick={onNavigateOverview} sx={{ flexShrink: 0 }}>Overview</Button>
              </Stack>
            </Box>

            <Box sx={{ p: 2, borderBottom: `1px solid ${BORDER}` }}>
              <Typography variant="subtitle2" sx={{ color: tokens.cream, mb: 1.25, fontFamily: editorialSerif }}>Customer Profile</Typography>
              <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.75, flexWrap: 'nowrap' }}>
                <Stack direction="row" spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
                  <Avatar sx={{ width: 56, height: 56, bgcolor: 'rgba(255,255,255,0.08)', fontSize: '1.1rem', flexShrink: 0 }}>
                    {(selectedUser?.name || selectedUser?.email || 'U')[0].toUpperCase()}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="h6" sx={{ color: tokens.cream }}>{selectedUser?.name || 'Unknown customer'}</Typography>
                      {selectedUser?.isEmailVerified && (
                        <CheckCircle sx={{ fontSize: '1rem', color: tokens.clay }} />
                      )}
                    </Stack>
                    <Typography variant="body2" sx={{ color: tokens.textSoft }}>{selectedUser?.email}</Typography>
                    <Typography variant="caption" sx={{ color: tokens.textMuted, display: 'block', mt: 0.3 }}>
                      Joined {formatCompactDate(selectedUser?.createdAt)} · {accountAgeDays} days
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                      <Chip label={formatStoreLabel(selectedSubscription)} size="small" sx={{ borderRadius: 0, height: 20, fontSize: '0.65rem' }} />
                      <Chip label={formatEntitlementLabel(selectedSubscription)} size="small" sx={{ borderRadius: 0, height: 20, fontSize: '0.65rem' }} />
                      <Chip label={getEffectiveRenewalStatus(selectedSubscription)} size="small" sx={{ borderRadius: 0, height: 20, fontSize: '0.65rem' }} />
                      <Chip
                        label={selectedProfile?.user?.timezone || 'US/Pacific'}
                        size="small"
                        sx={{ borderRadius: 0, height: 20, fontSize: '0.65rem' }}
                      />
                      {selectedUser?.isAdmin && <Chip label="Admin" size="small" sx={{ borderRadius: 0, height: 20, fontSize: '0.65rem' }} />}
                    </Stack>
                    {(onMakeAdmin || onRemoveAdmin) && selectedUser && (
                      <Stack direction="row" spacing={0.75} sx={{ mt: 1 }}>
                        {!selectedUser.isAdmin && onMakeAdmin && (
                          <Button size="small" variant="outlined" disabled={updating} onClick={() => onMakeAdmin(selectedUser.id)}>Make admin</Button>
                        )}
                        {selectedUser.isAdmin && onRemoveAdmin && (
                          <Button size="small" variant="outlined" color="warning" disabled={updating} onClick={() => onRemoveAdmin(selectedUser.id)}>Remove admin</Button>
                        )}
                      </Stack>
                    )}
                  </Box>
                </Stack>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '2px 28px',
                    minWidth: 280,
                    flexShrink: 0,
                  }}
                >
                  <MetadataCell label="User ID" value={selectedUser?.id ? `USR_${selectedUser.id.slice(0, 8)}` : '—'} />
                  <MetadataCell
                    label="Email Status"
                    value={selectedUser?.isEmailVerified ? 'Verified' : 'Unverified'}
                    valueColor={selectedUser?.isEmailVerified ? tokens.success : tokens.clay}
                  />
                  <MetadataCell
                    label="2FA"
                    value={selectedUser?.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                    valueColor={selectedUser?.twoFactorEnabled ? tokens.success : tokens.textMuted}
                  />
                  <MetadataCell
                    label="Last Active"
                    value={selectedProfile?.user?.lastActive ? formatRelativeTimeLong(selectedProfile.user.lastActive) : '—'}
                  />
                </Box>
              </Stack>
              <Box
                sx={{
                  display: 'flex',
                  border: `1px solid ${BORDER}`,
                  bgcolor: 'rgba(255,255,255,0.02)',
                  overflowX: 'auto',
                }}
              >
                <StatCell
                  icon={<LocalFireDepartment />}
                  label="Fasting Streak"
                  value={selectedProfile?.fasting?.streakDays != null ? `${selectedProfile.fasting.streakDays} days` : '—'}
                />
                <StatCell
                  icon={<Loop />}
                  label="Total Fasts"
                  value={selectedProfile?.fasting?.totalFasts}
                />
                <StatCell
                  icon={<Schedule />}
                  label="Avg. Fast Length"
                  value={selectedProfile?.fasting?.avgFastHours ? `${selectedProfile.fasting.avgFastHours} h` : '—'}
                />
                <StatCell
                  icon={<Opacity />}
                  label="Water Goal"
                  value={selectedProfile?.fasting?.waterGoalLiters ? `${selectedProfile.fasting.waterGoalLiters} L / day` : '—'}
                />
                <StatCell
                  icon={<MonitorWeight />}
                  label="Total Weight Loss"
                  value={selectedProfile?.fasting?.totalWeightLossLbs != null ? `${selectedProfile.fasting.totalWeightLossLbs} lbs` : '—'}
                />
              </Box>
            </Box>

            <Box sx={{ p: 2, flex: 1, minHeight: 0, overflowY: 'auto' }}>
              {selectedCase.source === 'support' && (
                <>
                  <ClientContextPanel context={selectedCase.raw.clientContext} />
                  <SupportEvidencePanel ticket={selectedCase.raw} />
                  <Typography variant="subtitle2" sx={{ color: tokens.cream, mb: 1.25, fontFamily: editorialSerif }}>Support Thread</Typography>
                  <Stack spacing={1} sx={{ mb: 2 }}>
                    {threadMessages.map((msg, i) => (
                      <ThreadMessage key={i} msg={msg} />
                    ))}
                    {(selectedCase.raw.internalNotes || []).map((note, i) => (
                      <Box key={`note-${i}`} sx={{ p: 1.25, border: `1px dashed ${BORDER}`, bgcolor: 'rgba(212,144,99,0.06)' }}>
                        <Typography variant="caption" sx={{ color: tokens.clay }}>Internal note · {note.adminEmail || 'Admin'}</Typography>
                        <Typography variant="body2" sx={{ color: tokens.textSoft }}>{note.text}</Typography>
                      </Box>
                    ))}
                  </Stack>
                  <Tabs value={replyMode} onChange={(_, v) => onReplyModeChange(v)} sx={{ mb: 1, minHeight: 32, '& .MuiTab-root': { minHeight: 32, py: 0.5, fontSize: '0.82rem' } }}>
                    <Tab label="Reply" value="reply" />
                    <Tab label="Internal Note" value="internal" />
                  </Tabs>
                  <TextField
                    multiline
                    minRows={3}
                    fullWidth
                    value={replyDraft}
                    onChange={(e) => onReplyChange(e.target.value)}
                    placeholder={replyMode === 'internal' ? 'Add an internal note for the team...' : 'Write the next customer-facing update...'}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
                  />
                  <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
                    <Button
                      variant="contained"
                      disabled={updating || !replyDraft.trim()}
                      onClick={replyMode === 'internal' ? onInternalNote : onSendReply}
                    >
                      {replyMode === 'internal' ? 'Save note' : 'Send reply'}
                    </Button>
                    <Button variant="outlined" disabled={updating} onClick={onMarkInProgress}>Mark in progress</Button>
                  </Stack>
                </>
              )}

              {selectedCase.source === 'submission' && (
                <>
                  <Typography variant="subtitle2" sx={{ color: tokens.cream, mb: 1.25 }}>Submission Review</Typography>
                  <Box sx={{ p: 1.5, border: `1px solid ${BORDER}`, mb: 2 }}>
                    <Typography variant="h6" sx={{ color: tokens.cream }}>{selectedCase.raw.productName}</Typography>
                    <Typography variant="body2" sx={{ color: tokens.textSoft, mb: 1 }}>Barcode: {selectedCase.raw.barcode}</Typography>
                    <Stack direction="row" spacing={1}>
                      <Button variant="contained" disabled={updating} onClick={onApproveSubmission}>Approve</Button>
                      <Button variant="outlined" color="error" disabled={updating} onClick={onRejectSubmission}>Reject</Button>
                    </Stack>
                  </Box>
                </>
              )}

              {selectedCase.source === 'subscription' && (
                <>
                  <Typography variant="subtitle2" sx={{ color: tokens.cream, mb: 1.25 }}>Subscription Resolution</Typography>
                  <Box sx={{ p: 1.5, border: `1px solid ${BORDER}`, mb: 2 }}>
                    <Typography variant="h6" sx={{ color: tokens.cream, textTransform: 'capitalize' }}>
                      {getEffectiveRenewalStatus(selectedSubscription)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: tokens.textSoft, mb: 1 }}>
                      {formatEntitlementLabel(selectedSubscription)} · {formatStoreLabel(selectedSubscription)}
                      {selectedSubscription?.trialStatus ? ` · trial ${selectedSubscription.trialStatus}` : ''}
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        gap: '2px 28px',
                        mb: 1.25,
                      }}
                    >
                      {subscriptionAuditSidebarRows(selectedSubscription).map(([label, value]) => (
                        <MetadataCell key={label} label={label} value={value} />
                      ))}
                    </Box>
                    <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
                      <Button variant="contained" disabled={updating} onClick={onGrantPremium}>Grant Premium</Button>
                      <Button variant="outlined" disabled={updating} onClick={onMarkExpired}>Mark expired</Button>
                    </Stack>
                  </Box>
                </>
              )}
            </Box>
          </>
        ) : (
          <Box sx={{ p: 3, display: 'grid', placeItems: 'center', flex: 1 }}>
            <Typography variant="body2" sx={{ color: tokens.textMuted }}>Select a case from the queue to start working.</Typography>
          </Box>
        )}
      </Box>

      {/* Right sidebar */}
      <Box sx={panelSx({ p: 2, width: 280, flexShrink: 0, minHeight: 0, height: '100%', overflowY: 'auto' })}>
        <Typography variant="subtitle2" sx={{ color: tokens.cream, mb: 1.5, fontFamily: editorialSerif }}>Subscription audit</Typography>
        <Stack spacing={0.9} sx={{ mb: 2 }}>
          {subscriptionAuditSidebarRows(selectedSubscription).map(([label, value]) => (
            <Stack key={label} direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
              <Typography variant="caption" sx={{ color: tokens.textMuted }}>{label}</Typography>
              <Typography variant="caption" sx={{ color: tokens.cream, textAlign: 'right', textTransform: label === 'Renewal' || label === 'Trial' || label === 'Entitlement' ? 'capitalize' : 'none' }}>
                {value}
              </Typography>
            </Stack>
          ))}
        </Stack>
        <Button
          fullWidth
          variant="outlined"
          size="small"
          sx={{ mb: 2 }}
          disabled={!getRevenuePlatformUrl(selectedSubscription)}
          onClick={() => {
            const url = getRevenuePlatformUrl(selectedSubscription);
            if (url) window.open(url, '_blank', 'noopener,noreferrer');
          }}
        >
          View in Revenue Platform
        </Button>

        <Divider sx={{ borderColor: BORDER, mb: 2 }} />

        <Typography variant="subtitle2" sx={{ color: tokens.cream, mb: 1.25, fontFamily: editorialSerif }}>Account Health</Typography>
        <Stack spacing={0.9} sx={{ mb: 2 }}>
          <AccountHealthItem label="No payment issues" ok={selectedSubscription?.status !== 'expired'} />
          <AccountHealthItem label="No open fraud flags" ok={selectedCase?.priority !== 'critical'} />
          <AccountHealthItem label="Email verified" ok={Boolean(selectedUser?.isEmailVerified)} />
          <AccountHealthItem label="Account active" ok={selectedUser?.isActive !== false && !selectedUser?.isBanned} />
          <AccountHealthItem
            label="Premium status aligned"
            ok={getEffectiveRenewalStatus(selectedSubscription) === 'active' ? selectedSubscription?.isPremium : true}
          />
          <AccountHealthItem
            label="Store verification recent"
            ok={!selectedSubscription?.lastVerifiedAt || (Date.now() - new Date(selectedSubscription.lastVerifiedAt).getTime()) < 7 * 24 * 60 * 60 * 1000}
          />
        </Stack>

        <Divider sx={{ borderColor: BORDER, mb: 2 }} />

        <Typography variant="subtitle2" sx={{ color: tokens.cream, mb: 1.25, fontFamily: editorialSerif }}>Actions</Typography>
        <Stack spacing={0.75} sx={{ mb: 2 }}>
          <Button variant="contained" fullWidth disabled={updating || !selectedUser} onClick={onSyncStore}>Sync with Store</Button>
          <Button variant="outlined" fullWidth disabled={updating || !selectedUser} onClick={onGrantPremium}>Grant Premium (Manual)</Button>
          <Button variant="outlined" fullWidth disabled={updating || !selectedUser} onClick={onRefund}>Issue Refund</Button>
          <Button variant="outlined" fullWidth disabled={updating || !selectedUser} onClick={() => setCreditDialogOpen(true)}>Add Account Credit</Button>
          <Button variant="outlined" fullWidth color="warning" disabled={updating || !selectedUser} onClick={onSuspendAccount}>
            {selectedUser?.isActive === false ? 'Restore Account' : 'Suspend Account'}
          </Button>
          <Button variant="contained" fullWidth color="error" disabled={updating || !selectedUser} onClick={onBanAccount}>
            {selectedUser?.isBanned ? 'Unban Account' : 'Ban Account'}
          </Button>
          <Button variant="outlined" fullWidth color="error" disabled={updating || !selectedUser} onClick={onCancelSubscription}>Cancel Subscription</Button>
        </Stack>

        <Divider sx={{ borderColor: BORDER, mb: 1.5 }} />

        <Typography variant="subtitle2" sx={{ color: tokens.cream, mb: 1, fontFamily: editorialSerif }}>Case Tags</Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {activeTags.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              onDelete={() => onCaseTagsChange(activeTags.filter((t) => t !== tag))}
              sx={{ borderRadius: 0, height: 22, fontSize: '0.72rem' }}
            />
          ))}
          <Chip
            label="+ Add"
            size="small"
            variant="outlined"
            onClick={() => { setTagDraft(''); setTagDialogOpen(true); }}
            sx={{ borderRadius: 0, height: 22, fontSize: '0.72rem' }}
          />
        </Stack>
      </Box>

      <Dialog open={tagDialogOpen} onClose={() => setTagDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Add case tag</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Tag"
            value={tagDraft}
            onChange={(e) => setTagDraft(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTagDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              const next = tagDraft.trim();
              if (next && !activeTags.includes(next)) onCaseTagsChange([...activeTags, next]);
              setTagDialogOpen(false);
            }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={creditDialogOpen} onClose={() => setCreditDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Add account credit</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Amount (USD)"
            type="number"
            inputProps={{ min: 0.01, step: 0.01 }}
            value={creditAmountDollars}
            onChange={(e) => setCreditAmountDollars(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={updating}
            onClick={() => {
              const cents = Math.round(Number(creditAmountDollars) * 100);
              if (cents > 0) {
                onAddCredit?.(cents);
                setCreditDialogOpen(false);
              }
            }}
          >
            Add credit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
